import React, { useState } from "react";
import ScoreTrace from "./ScoreTrace";

const money = (value) => (value === null || value === undefined || value === "" ? "—" : value);

// current_value is wiped to nil by a failing upstream layer (see
// Layers::UnitDataSetter#fail!), so fall back to original_value for
// display -- an unresolved field is still worth showing, just unmatched.
const fieldValue = (unit) => {
  if (!unit) return null;
  const current = unit.current_value;
  if (current !== null && current !== undefined && current !== "") return current;
  return unit.original_value;
};

const MatchBadge = ({ unit }) => {
  if (!unit) return null;
  const matched = unit.ref !== null && unit.ref !== undefined && unit.ref !== "";
  return (
    <span className={`badge ${matched ? "bg-success" : "bg-secondary"} ms-1`} style={{ fontSize: "10px" }}>
      {matched ? `matched · ${unit.score}` : "unmatched"}
    </span>
  );
};

const CHARGE_FIELDS = [
  ["sales_tax", "Sales Tax"],
  ["surcharge", "Surcharge"],
  ["shipping_fee", "Shipping"],
  ["deposit", "Deposit"],
  ["other_fees", "Other Fees"],
  ["discount", "Discount"],
  ["credit", "Credit"],
];

// Table-shaped view of Layers::Synthesize / Layers::R365Synthesize output
// (Step 5's "Map data" result). Every field here is a Layers::UnitData hash
// ({current_value, original_value, score, passed, ref, components, ...}),
// unlike InvoicePreview's flat extraction shape -- hence a separate component
// rather than reusing InvoicePreview directly.
const MappedInvoicePreview = ({ data }) => {
  const [showVendorTrace, setShowVendorTrace] = useState(false);
  const vendorTrace = data.vendor && data.vendor.score_trace;
  const lineItems = data.line_items || [];
  const location = fieldValue(data.location);
  const charges = CHARGE_FIELDS.map(([key, label]) => [key, label, fieldValue(data[key])]).filter(
    ([, , value]) => value !== null && value !== undefined && value !== ""
  );

  return (
    <div className="border rounded p-3 bg-white mt-2">
      <div className="d-flex justify-content-between align-items-start border-bottom pb-2 mb-2">
        <div>
          <div className="fw-bold">
            {fieldValue(data.vendor) || "(vendor not captured)"}
            <MatchBadge unit={data.vendor} />
            {vendorTrace && vendorTrace.length > 0 && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-2 fw-normal"
                style={{ fontSize: "11px" }}
                onClick={() => setShowVendorTrace(!showVendorTrace)}
              >
                {showVendorTrace ? "▾ hide" : "▸ how scored"}
              </button>
            )}
          </div>
          <div className="text-muted small">Mapped Invoice</div>
          {showVendorTrace && (
            <div className="border rounded mt-1">
              <ScoreTrace trace={vendorTrace} title="How the vendor match score was reached" />
            </div>
          )}
        </div>
        <div className="text-end small">
          <div>
            Invoice #: <strong>{fieldValue(data.invoice_number) || "—"}</strong>
          </div>
          <div>
            Date: <strong>{fieldValue(data.invoice_date) || "—"}</strong>
          </div>
        </div>
      </div>

      {location && (
        <div className="small mb-3">
          <div className="text-muted text-uppercase" style={{ fontSize: "10px" }}>
            Location
          </div>
          <div>{location}</div>
        </div>
      )}

      {lineItems.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-2">
            <thead className="table-light">
              <tr>
                <th>Item #</th>
                <th>Description</th>
                <th className="text-end">Qty</th>
                <th className="text-end">Unit Price</th>
                <th className="text-end">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <tr key={i}>
                  <td>
                    {fieldValue(item.item_number)}
                    <MatchBadge unit={item.item_number} />
                  </td>
                  <td>{fieldValue(item.description)}</td>
                  <td className="text-end">{fieldValue(item.quantity)}</td>
                  <td className="text-end">{fieldValue(item.unit_price)}</td>
                  <td className="text-end">{fieldValue(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-muted small mb-2">No line items captured.</div>
      )}

      <div className="d-flex justify-content-end">
        <table className="small" style={{ minWidth: "220px" }}>
          <tbody>
            {charges.map(([key, label, value]) => (
              <tr key={key}>
                <td className="text-muted pe-3">{label}</td>
                <td className="text-end">{money(value)}</td>
              </tr>
            ))}
            <tr className="border-top fw-bold">
              <td className="pe-3">Total</td>
              <td className="text-end">{money(fieldValue(data.total_amount))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MappedInvoicePreview;
